import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

type ResolvedCategory = components["schemas"]["ResolvedCategoryResponse"];

export interface CategoryComboboxProps {
  /** Currently selected category `key`, or null when unclassified. */
  value: string | null;
  onChange: (key: string | null) => void;
  /** Resolved taxonomy from `GET /payables/categories` (system defaults first, then client rows). */
  categories: ResolvedCategory[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

/** The value is the category's stable `key`, never an FK. An unknown key (e.g. archived) shows raw so the row never reads empty. */
export function CategoryCombobox({
  value,
  onChange,
  categories,
  placeholder = "Select category…",
  searchPlaceholder = "Search categories…",
  emptyText = "No categories found",
  disabled,
  className,
}: CategoryComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => categories.find((c) => c.key === value) ?? null,
    [categories, value],
  );

  const { system, client } = useMemo(() => {
    const system: ResolvedCategory[] = [];
    const client: ResolvedCategory[] = [];
    for (const c of categories) {
      (c.source === "client" ? client : system).push(c);
    }
    return { system, client };
  }, [categories]);

  const renderItem = (cat: ResolvedCategory) => (
    <CommandItem
      key={cat.key}
      // cmdk filters on this string — include name + key so both are searchable.
      value={`${cat.displayName} ${cat.key}`}
      onSelect={() => {
        onChange(cat.key);
        setOpen(false);
      }}
    >
      <Check
        className={cn("mr-2 size-4", value === cat.key ? "opacity-100" : "opacity-0")}
      />
      <span className="min-w-0 flex-1 truncate">{cat.displayName}</span>
      {cat.defaultSpendClass && (
        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
          {cat.defaultSpendClass}
        </span>
      )}
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full min-w-0 justify-between font-normal",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selected ? selected.displayName : value || placeholder}
          </span>
          {value ? (
            <span
              role="button"
              aria-label="Clear category"
              className="ml-2 rounded-full p-0.5 hover:bg-muted-foreground/20"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }}
            >
              <X className="size-3.5 opacity-60" />
            </span>
          ) : (
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {system.length > 0 && (
              <CommandGroup heading="Dolfin defaults">{system.map(renderItem)}</CommandGroup>
            )}
            {client.length > 0 && (
              <CommandGroup heading="Custom categories">{client.map(renderItem)}</CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
